import { toInventoryList } from '../api/inventory-request';
import { DEMO_IPD_PRESCRIPTION } from '../pages/pharmacy/demoIpdPrescription';
import {
  MOCK_BILLS,
  MOCK_WARD_RETURNS,
} from '../pages/pharmacy/pharmacyMockData';

export const DEMO_PRESCRIPTION_ID = DEMO_IPD_PRESCRIPTION.id;
export const DEMO_PRESCRIPTION_UUID = String(DEMO_PRESCRIPTION_ID);
export const DEMO_ORDER_NUMBER = DEMO_IPD_PRESCRIPTION.prescription_number || DEMO_IPD_PRESCRIPTION.uhid;

export function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
}

export function isDemoPrescriptionId(id) {
  if (!id) return true;
  return String(id) === String(DEMO_PRESCRIPTION_ID)
    || id === DEMO_ORDER_NUMBER
    || id === DEMO_IPD_PRESCRIPTION.uhid;
}

function uiBillToApi(row) {
  return {
    bill_id: row.id,
    transaction_id: row.dispense_id,
    patient_name: row.patient_name,
    amount: row.amount,
    payment_mode: row.payment_mode,
    transaction_date: row.date,
    billing_status: row.status,
  };
}

function uiWardReturnToApi(row) {
  return {
    id: row.id,
    ward_name: row.ward,
    item_name: row.medicine,
    batch_number: row.batch,
    return_quantity: row.qty,
    requested_by: row.raised_by,
    transaction_date: row.date,
    status: row.status,
  };
}

export const FALLBACK_PRESCRIPTIONS = [{ ...DEMO_IPD_PRESCRIPTION }];

export const FALLBACK_BILLS = [
  uiBillToApi(MOCK_BILLS[0]),
];

export const FALLBACK_WARD_RETURNS = [
  uiWardReturnToApi(MOCK_WARD_RETURNS[0]),
];

export function fallbackPrescriptionDetail(id) {
  if (!isDemoPrescriptionId(id)) return null;
  return { ...DEMO_IPD_PRESCRIPTION };
}

/**
 * Whether a failed pharmacy API call should fall back to demo data instead of
 * surfacing the error. Covers 404 as well as the backend's various "can't
 * serve real data" failure modes (missing org context, DB unreachable, etc.,
 * which this API returns as 400/500) so the dashboard stays usable while the
 * feed isn't fully wired up. A 401 is excluded — that means the session
 * itself is invalid and should surface rather than be masked by fake data.
 */
export function isPharmacyNotFound(err) {
  const status = err?.status ?? err?.response?.status;
  return status !== 401;
}

/**
 * Stricter variant for the IPD prescription feed: only a genuine 404 seeds demo
 * data. 403/500/network are real failures a pharmacist must see, and the broad
 * rule above turned them into a screen full of plausible fake orders with working
 * Start Dispense buttons. Scoped to this feed rather than applied to
 * isPharmacyNotFound, which the controlled-queue and bills tabs still rely on.
 */
export function isPrescriptionFeedMissing(err) {
  const status = err?.status ?? err?.response?.status;
  return status === 404;
}

/** Is this list the seeded demo row rather than real data? Drives the "Sample data" notice. */
export function isDemoPrescriptionList(rows) {
  if (!Array.isArray(rows) || rows.length !== 1) return false;
  const id = rows[0]?.id ?? rows[0]?.prescription_id;
  return id != null && String(id) === DEMO_PRESCRIPTION_UUID;
}

export function withListFallback(payload, seedRows) {
  if (!seedRows?.length) return payload;
  if (toInventoryList(payload).length > 0) return payload;

  if (Array.isArray(payload)) return [...seedRows];
  if (payload && typeof payload === 'object') {
    if (Object.prototype.hasOwnProperty.call(payload, 'items')) {
      return { ...payload, items: [...seedRows] };
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
      return { ...payload, data: [...seedRows] };
    }
  }
  return [...seedRows];
}

function hasPrescriptionDetail(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const id = payload.id || payload.prescription_id;
  if (!id) return false;
  return Array.isArray(payload.medicines) || Array.isArray(payload.lines);
}

export function withDetailFallback(payload, seedRow) {
  if (!seedRow) return payload;

  if (Array.isArray(payload)) {
    return payload.length > 0 ? payload[0] : { ...seedRow };
  }

  if (payload && typeof payload === 'object') {
    const nested = payload.data;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      if (hasPrescriptionDetail(nested)) return nested;
    }

    const list = toInventoryList(payload);
    if (list.length > 0) return list[0];

    if (hasPrescriptionDetail(payload)) return payload;
  }

  return { ...seedRow };
}
